/**
 * Guarded asset loading. The asset stream produces textures/atlas concurrently
 * and a file may be absent in some runs, so loading never THROWS — a miss degrades
 * to a solid-color palette placeholder material instead of aborting the React tree.
 *
 * Two flavors:
 *  • SUSPENDING (`useSuspendingTexture` / `useSuspendingAtlasMeta`) — the default
 *    for assets that reliably ship (room carpet/wall, table felt/wood, card atlas).
 *    They suspend (via the nearest <Suspense>) until the asset loads, so materials
 *    are ALWAYS created WITH their maps present on the first render. This is what
 *    makes texture visibility DETERMINISTIC across cold and warm loads — see the
 *    long note on `useSuspendingTexture` for the underlying three.js reason (adding
 *    a `map` to an already-compiled material needs a shader recompile that a plain
 *    prop assignment does NOT trigger). A genuinely-absent file resolves to `null`,
 *    so even the suspending path keeps a graceful fallback.
 *  • NON-SUSPENDING (`useGuardedTexture`) — kept for cases where a one-frame
 *    suspend is undesirable and a flat placeholder forever is acceptable. NOTE: it
 *    is subject to the add-map-post-compile pitfall above, so do NOT use it to feed
 *    a material `map` that must appear on a cold load.
 */

import { useEffect, useState } from 'react';
import { useLoader } from '@react-three/fiber';
import * as THREE from 'three';

/** Whether a texture carries color data (sRGB) or linear data (normal/rough/etc). */
export type TextureKind = 'color' | 'linear';

/** Per-map tiling config applied to a suspended texture before first render. */
export interface TextureConfig {
  /** sRGB (albedo) vs linear (normal/roughness/etc). */
  readonly kind?: TextureKind;
  /** RepeatWrapping repeat counts; omit to leave at the default (1,1) clamp. */
  readonly repeat?: readonly [number, number];
}

/** Module-level texture cache so we load each path at most once. */
const textureCache = new Map<string, THREE.Texture | null>();
const inflight = new Map<string, Promise<THREE.Texture | null>>();

function loadTextureOnce(url: string, kind: TextureKind): Promise<THREE.Texture | null> {
  const cached = textureCache.get(url);
  if (cached !== undefined) return Promise.resolve(cached);
  const existing = inflight.get(url);
  if (existing) return existing;

  const loader = new THREE.TextureLoader();
  const p = new Promise<THREE.Texture | null>((resolve) => {
    loader.load(
      url,
      (tex) => {
        // Normal/roughness/metalness maps must stay linear; only albedo is sRGB.
        tex.colorSpace = kind === 'color' ? THREE.SRGBColorSpace : THREE.NoColorSpace;
        textureCache.set(url, tex);
        inflight.delete(url);
        resolve(tex);
      },
      undefined,
      () => {
        // File absent — cache the miss so we don't retry every render.
        textureCache.set(url, null);
        inflight.delete(url);
        resolve(null);
      },
    );
  });
  inflight.set(url, p);
  return p;
}

/**
 * Non-suspending texture hook. Returns `undefined` until resolved, then the
 * `THREE.Texture` or `null` if the file is missing. The caller uses `null` as
 * the signal to fall back to a solid color. Disposes nothing here (textures are
 * shared via the cache for the app lifetime — a deliberate small leak avoided by
 * the single-room, single-mount client).
 */
export function useGuardedTexture(
  url: string | undefined,
  kind: TextureKind = 'color',
): THREE.Texture | null | undefined {
  const [tex, setTex] = useState<THREE.Texture | null | undefined>(() =>
    url ? (textureCache.get(url) ?? undefined) : null,
  );

  useEffect(() => {
    if (!url) {
      setTex(null);
      return;
    }
    let alive = true;
    void loadTextureOnce(url, kind).then((t) => {
      if (alive) setTex(t);
    });
    return () => {
      alive = false;
    };
  }, [url, kind]);

  return tex;
}

/**
 * SUSPENDING texture hook — the correct loader for assets that reliably exist on
 * disk (room carpet/wall, table felt/wood). It suspends the component (via the
 * nearest <Suspense>) until the texture has loaded, so the material is ALWAYS
 * constructed with its `map`/`normalMap`/`roughnessMap` PRESENT on the very first
 * render.
 *
 * WHY THIS (not the non-suspending hook) FIXES THE COLD-LOAD BUG:
 * `MeshStandardMaterial` compiles its shader program from the set of maps present
 * the first time the renderer draws it. Adding a `map` to an ALREADY-COMPILED
 * material requires `material.needsUpdate = true` to force a recompile (the map
 * changes the program's `#define USE_MAP` + UV varyings); merely assigning
 * `material.map` does NOT bump `needsUpdate`, so the texture is uploaded but never
 * sampled. The old non-suspending hook returned `undefined` on a cold first render
 * → the material compiled WITHOUT the map → the late-arriving texture was ignored
 * (flat fallback). On a warm load the cache was primed, so the map was present at
 * first render and it worked — hence the warm/cold split. Suspending until the
 * texture exists removes the post-compile mutation entirely: deterministic on
 * every load.
 *
 * Tolerant by design: the underlying load NEVER rejects — a missing file resolves
 * to `null` (graceful palette fallback) rather than throwing into the Suspense
 * boundary (which has no error boundary). So this is safe even if an asset is
 * absent; the only cost of absence is a one-frame suspend.
 *
 * Returns the configured `THREE.Texture`, or `null` if the file was absent. Never
 * `undefined` (it suspends instead).
 */
export function useSuspendingTexture(
  url: string,
  config: TextureConfig = {},
): THREE.Texture | null {
  const { kind = 'color', repeat } = config;
  // useLoader caches per (loader, url); our loader's error path resolves to a
  // sentinel rather than rejecting, so the boundary never sees an error.
  const tex = useLoader(SafeTextureLoader, url);
  if (tex) {
    // Apply config ONCE per (cached) texture. The texture is shared via useLoader's
    // cache, so guard with a stamp to avoid re-uploading the image on every render.
    const want = `${kind}|${repeat ? `${repeat[0]}x${repeat[1]}` : 'clamp'}`;
    if (tex.userData.cfg !== want) {
      tex.colorSpace = kind === 'color' ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      if (repeat) {
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(repeat[0], repeat[1]);
      }
      tex.needsUpdate = true; // re-upload once so colorSpace + wrap take effect
      tex.userData.cfg = want;
    }
  }
  return tex;
}

/**
 * A loader (the shape `useLoader` consumes: a `Loader` with `load(url, onLoad,
 * onProgress, onError)`) whose error path resolves to `null` instead of erroring,
 * so `useLoader` — and thus Suspense — never REJECTS into the boundary (which has
 * no error boundary). It WRAPS a `THREE.TextureLoader` rather than subclassing it,
 * so its `load` signature is free to surface `null`. The non-null texture is
 * returned as-is; colorSpace/repeat are applied by the hook (per call site, not
 * per cache entry). useLoader caches per (loaderCtor, url).
 */
class SafeTextureLoader extends THREE.Loader<THREE.Texture | null> {
  private readonly inner = new THREE.TextureLoader();

  override load(
    url: string,
    onLoad?: (data: THREE.Texture | null) => void,
    onProgress?: (event: ProgressEvent) => void,
    _onError?: (err: unknown) => void,
  ): void {
    this.inner.load(
      url,
      (tex) => onLoad?.(tex),
      onProgress,
      () => onLoad?.(null),
    );
  }
}

/**
 * Card-atlas metadata, matching the asset stream's `atlas.json`. Frames are keyed
 * `RANK_SUITLETTER` (e.g. `A_C`, `10_S`) and carry precomputed UVs
 * (`u0,v0` = top-left, `u1,v1` = bottom-right in 0..1 image space). Guarded.
 */
export interface AtlasFrame {
  readonly rank: string;
  readonly suit: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly u0: number;
  readonly v0: number;
  readonly u1: number;
  readonly v1: number;
}
export interface AtlasMeta {
  readonly frames: Record<string, AtlasFrame>;
  readonly meta: { readonly atlasSize: readonly [number, number]; readonly cardSize: readonly [number, number] };
}

const atlasMetaCache = new Map<string, AtlasMeta | null>();

/** Probe the atlas JSON; resolves `null` if absent so cards use a solid back. */
export function useGuardedAtlasMeta(url: string | undefined): AtlasMeta | null | undefined {
  const [meta, setMeta] = useState<AtlasMeta | null | undefined>(() =>
    url ? (atlasMetaCache.get(url) ?? undefined) : null,
  );

  useEffect(() => {
    if (!url) {
      setMeta(null);
      return;
    }
    const cached = atlasMetaCache.get(url);
    if (cached !== undefined) {
      setMeta(cached);
      return;
    }
    let alive = true;
    void fetch(url)
      .then((r) => (r.ok ? (r.json() as Promise<AtlasMeta>) : null))
      .catch(() => null)
      .then((m) => {
        atlasMetaCache.set(url, m);
        if (alive) setMeta(m);
      });
    return () => {
      alive = false;
    };
  }, [url]);

  return meta;
}

/** Module promise cache for the suspending atlas-meta hook. */
const atlasMetaPromises = new Map<string, Promise<void>>();

/**
 * SUSPENDING atlas-meta hook. Suspends until the JSON has loaded (or is known
 * absent), so the card material's per-instance UVs are computed with the real
 * frames on the first layout pass — no cold-load "show back, then flip to face"
 * frame. Resolves `null` (never throws) when the file is absent so cards fall
 * back to the solid back.
 */
export function useSuspendingAtlasMeta(url: string): AtlasMeta | null {
  const cached = atlasMetaCache.get(url);
  if (cached !== undefined) return cached;
  let p = atlasMetaPromises.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => (r.ok ? (r.json() as Promise<AtlasMeta>) : null))
      .catch(() => null)
      .then((m) => {
        atlasMetaCache.set(url, m);
      });
    atlasMetaPromises.set(url, p);
  }
  // React Suspense suspends by THROWING the pending promise (not an Error); the
  // boundary re-renders when it settles and the cache is then populated.
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw p;
}
